import { useRef, type RefObject } from 'react';
import { useGetScreensInfiniteQuery } from './hooks/useGetScreensInfiniteQuery';
import { useGetScreensMetaQuery } from './hooks/useGetScreensMetaQuery';
import type { AtomicRendererRefCurrent } from './types';
import { Flex, Spinner } from '@banhala/ui-kit';

import { useScrollToFilterList } from './hooks/useScrollToFilterList';
import { useLogViewEvent } from './hooks/useLogViewEvent';
import type { SetState } from 'shared/src/types';
import type { ScreensMetaResponse } from 'shared/src/api';
import { skeletonColumnTypeFromApiValue } from 'shared/src/api';
import { useSetTitle } from './hooks/useSetTitle';
import { useSetDropdownNavigator } from './hooks/useSetDropdownNavigator';
import { AtomicRenderer, useFilterListAtom } from 'shared/src/component';
import { useGetCommonParams } from '../hooks';

type ScreensResultProps = {
  setTitle?: SetState<string | undefined>;
  setDropdownNavigator?: SetState<
    ScreensMetaResponse['screen']['dropdown_navigator'] | null
  >;
  onScrollY?: (scrollY: number) => void;
  onBeginDrag?: (scrollY: number) => void;
  onEndDrag?: (scrollY: number) => void;
  listRef?: RefObject<AtomicRendererRefCurrent>;
  hideScreenToTop?: boolean;
};

export const ScreensResult = ({
  setTitle,
  setDropdownNavigator,
  onScrollY,
  onBeginDrag,
  onEndDrag,
  hideScreenToTop,
  listRef: externalListRef,
}: ScreensResultProps) => {
  const internalListRef = useRef<AtomicRendererRefCurrent>(null);
  const atomicRendererRef = externalListRef ?? internalListRef;
  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const { nextToken } = useGetCommonParams();

  const { data, fetchNextPage, isFetching, isLoading, refetch } =
    useGetScreensInfiniteQuery(nextToken);
  const { filterListAtomValue } = useFilterListAtom();
  const { data: metaData } = useGetScreensMetaQuery();
  // iOS `MetaFeedReactor.fetchScreenAppreanceData` / Android `ComponentMetaViewModel`
  // 와 1:1 대칭. 서버 MetaScreenSerializer default가 "2col"이라 모든 화면에서 값이 내려옴.
  const skeletonType = skeletonColumnTypeFromApiValue(
    metaData?.screen.skeleton_type,
  );
  useLogViewEvent({ logging: data?.logging, screenName: data?.screenName });
  const {
    filterListIndex,
    scrollToFilterList,
    onItemHeightChange,
    isPendingScroll,
  } = useScrollToFilterList({
    atomicRendererRef,
    components: data?.components,
    isFetching,
    itemHeightsRef,
  });
  useSetTitle({ setTitle, title: data?.title });
  useSetDropdownNavigator({
    setDropdownNavigator,
    dropdownNavigator: metaData?.screen.dropdown_navigator,
  });

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Flex flex={1}>
      {isFetching && (
        <Flex.Center
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          backgroundColor="bg_primary"
          backgroundColorOpacity={isPendingScroll ? 0.5 : 0}
          zIndex={1000}
          pointerEvents={isPendingScroll ? undefined : 'none'}
        >
          <Spinner size={20} />
        </Flex.Center>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={atomicRendererRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        isPendingScroll={isPendingScroll}
        itemHeightsRef={itemHeightsRef}
        onItemHeightChange={onItemHeightChange}
        onScrollY={onScrollY}
        onBeginDrag={onBeginDrag}
        onEndDrag={onEndDrag}
        filterParams={filterListAtomValue}
        skeletonType={skeletonType}
        hideScrollToTop={hideScreenToTop}
      />
    </Flex>
  );
};
